import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X } from 'lucide-react';
import api from '../lib/api';

// Generate or get visitor ID
const getVisitorId = () => {
  let visitorId = localStorage.getItem('akashica_visitor_id');
  if (!visitorId) {
    visitorId = 'v_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
    localStorage.setItem('akashica_visitor_id', visitorId);
  }
  return visitorId;
};

// Check if tracking is allowed
const isTrackingAllowed = () => {
  return localStorage.getItem('akashica_tracking_consent') === 'true';
};

// Track page visit
export const trackPageVisit = async (page, landing = null) => {
  if (!isTrackingAllowed()) return;
  
  try {
    await api.post('/track/visit', {
      page,
      visitor_id: getVisitorId(),
      landing,
      source: document.referrer ? null : 'direct',
      referrer: document.referrer || null
    });
  } catch (error) {
    console.error('Tracking error:', error);
  }
};

// Track conversion
export const trackConversion = async (conversionType, landing = null, amount = null) => {
  if (!isTrackingAllowed()) return;
  
  try {
    await api.post('/track/conversion', {
      visitor_id: getVisitorId(),
      conversion_type: conversionType,
      landing,
      amount
    });
  } catch (error) {
    console.error('Conversion tracking error:', error);
  }
};

// Cookie Banner Component
export const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('akashica_tracking_consent');
    if (consent === null) {
      setShowBanner(true);
    }
  }, []);

  const acceptCookies = () => {
    localStorage.setItem('akashica_tracking_consent', 'true');
    setShowBanner(false);
    // Track current page now that we have consent
    trackPageVisit(window.location.pathname);
  };

  const rejectCookies = () => {
    localStorage.setItem('akashica_tracking_consent', 'false');
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 p-4 bg-white border-t border-[#e8e0d0] shadow-[0_-4px_20px_rgba(42,32,16,0.08)]" data-testid="cookie-banner">
      <div className="max-w-5xl mx-auto flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div className="flex items-start gap-3 flex-1">
          <div className="w-10 h-10 rounded-full bg-[#c9a227]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
            <Cookie className="w-5 h-5 text-[#c9a227]" strokeWidth={1.5} />
          </div>
          <div className="text-[13px] text-[hsl(var(--text-secondary))] leading-relaxed">
            <p>
              Usamos cookies propias técnicas (imprescindibles) y de analítica propia para entender cómo se usa el sitio. No usamos analítica de terceros ni publicidad. Puedes aceptar, rechazar, o consultar más en nuestra{' '}
              <Link to="/cookies" className="text-[#c9a227] hover:underline font-medium" data-testid="banner-cookies-link">Política de cookies</Link>
              {' '}y en la{' '}
              <Link to="/privacidad" className="text-[#c9a227] hover:underline font-medium" data-testid="banner-privacy-link">Política de privacidad</Link>.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={rejectCookies}
            className="px-5 py-2.5 text-sm font-sans text-[hsl(var(--text-secondary))] border border-[#d4c8a8] hover:bg-[#faf6ed] rounded-full transition-colors"
            data-testid="cookie-reject"
          >
            Rechazar
          </button>
          <button
            onClick={acceptCookies}
            className="px-5 py-2.5 text-sm font-sans font-medium bg-[#c9a227] text-white rounded-full hover:bg-[#b89220] transition-colors"
            data-testid="cookie-accept"
          >
            Aceptar
          </button>
        </div>
      </div>
    </div>
  );
};

// Page Tracker Component - Add to pages you want to track
export const PageTracker = ({ page, landing }) => {
  useEffect(() => {
    if (isTrackingAllowed()) {
      trackPageVisit(page, landing);
    }
  }, [page, landing]);

  return null;
};

export default { trackPageVisit, trackConversion, CookieBanner, PageTracker, getVisitorId, isTrackingAllowed };
